import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Puzzle, Heart, Sun, Star, Music, Palette, 
  MapPin, Phone, Mail, ArrowRight, Menu, X,
  Users, Trophy, School, CheckCircle2, BookOpen,
  Facebook, Instagram, Linkedin, Youtube, Clock, Info,
  Globe, GraduationCap, ChevronLeft, ChevronRight, ChevronDown, 
  PlayCircle, Camera, Calendar, Download, Quote, Send, ImagePlus,
  FileText, Plus, Building2, Image as ImageIcon, ArrowUpRight,
  MessageCircle, Award, Target, HelpCircle
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

// =============================================
// DESIGN 2 — VIBRANT KIDS
// Backbone: 100% identical to Design0_Features
// UI: Original VibrantKids visual design intact
// =============================================

const VibrantKids: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  // ── BACKBONE STATE (from Design0) ──────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedView, setExpandedView] = useState<'staff' | 'board' | 'gallery' | 'facilities' | 'anthems' | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── VIBRANT KIDS ORIGINAL STATE ─────────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // ── BACKBONE EFFECTS (from Design0) ───────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const safeUrl = (file: any) => {
    if (!file) return undefined;
    if (typeof file === 'string') return file;
    try { return URL.createObjectURL(file); } catch (e) { return undefined; }
  };

  // ── BACKBONE DATA OBJECT — EXACT COPY FROM DESIGN0 ────────────────────────
  const data = {
    name: previewData?.name || "St. Augustine's National Academy",
    motto: previewData?.motto || "In Veritate et Virtute (In Truth and Virtue)",
    logo: previewData?.logo ? safeUrl(previewData.logo) : "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=256&auto=format&fit=crop",
    headerPhotos: (previewData?.headerPhotos && previewData.headerPhotos.filter((p: any) => p !== null).length > 0) ? previewData.headerPhotos : [
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=1200&auto=format&fit=crop"
    ],
    stats: {
      students: previewData?.totalStudents || "1,450",
      staff: previewData?.totalStaff || "92",
      founded: previewData?.yearFounded || "1974",
      alumni: previewData?.totalAlumni || "8,000+"
    },
    principal: {
      name: previewData?.principalName || "Dr. Richard M. Mutua",
      message: previewData?.principalMessage || "Welcome to St. Augustine's, a leading institution where we harmonize academic discipline with character development to mold the leaders of tomorrow.",
      photo: previewData?.principalPhoto ? safeUrl(previewData.principalPhoto) : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop"
    },
    mission: previewData?.mission || "To provide a holistic and inclusive learning environment that fosters academic brilliance, integrity, and global citizenship.",
    vision: previewData?.vision || "To be the premier national center of excellence, recognized globally for academic innovation and transformative leadership.",
    about: previewData?.about || "A distinguished national institution dedicated to the pursuit of excellence in all spheres of life.",
    history: previewData?.historyText || "Established in 1974 as a community initiative, our school was later elevated to National status in 2011, continuing a heritage of producing top-tier professionals and leaders for the continent.",
    aboutPhoto: previewData?.aboutPhoto ? safeUrl(previewData.aboutPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070",
    missionPhoto: previewData?.missionPhoto ? safeUrl(previewData.missionPhoto) : "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070",
    visionPhoto: previewData?.visionPhoto ? safeUrl(previewData.visionPhoto) : "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=2070",
    whyChooseUsPhoto: previewData?.whyChooseUsPhoto ? safeUrl(previewData.whyChooseUsPhoto) : "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2070",
    nationalAnthem: previewData?.nationalAnthem || "O God of all creation\nBless this our land and nation\nJustice be our shield and defender\nMay we dwell in unity\nPeace and liberty\nPlenty be found within our borders.\n\nLet one and all arise\nWith hearts both strong and true\nService be our earnest endeavour\nAnd our homeland of Kenya\nHeritage of splendour\nFirm may we stand to defend.\n\nLet all with one accord\nIn common bond united\nBuild this our nation together\nAnd the glory of Kenya\nThe fruit of our labour\nFill every heart with thanksgiving.",
    schoolAnthem: previewData?.schoolAnthem || "Hail to our alma mater, standing tall and true,\nGuiding lights of knowledge, red and brilliant blue.\nIn these halls of wisdom, we learn to lead and serve,\nBuilding strong foundations, with unyielding nerve.\n\nThrough the trials and triumphs, together we will rise,\nReaching for the future, beneath the open skies.\nHonor, truth, and courage, forever be our guide,\nIn our school's great legacy, we take eternal pride!",
    whyUs: previewData?.whyChooseUs || "We combine a tradition of excellence with state-of-the-art facilities and a passionate faculty dedicated to cross-disciplinary learning.",
    coreValues: previewData?.coreValues || ["Integrity", "Excellence", "Innovation", "Inclusivity", "Service"],
    quote: {
      text: previewData?.quote || "The beautiful thing about learning is that no one can take it away from you.",
      author: previewData?.quoteAuthor || "B.B. King"
    },
    curriculum: {
      description: previewData?.curriculumDesc || "Our comprehensive curriculum follows the National Competency Based framework, enhanced with international STEM integration for a global perspective.",
      compulsory: previewData?.compulsorySubjects || ["English", "Mathematics", "Kiswahili", "Chemistry", "Biology", "Physics"],
      optional: previewData?.optionalSubjects || ["Computer Studies", "French", "Business Studies", "Agriculture", "Music", "Aviation"],
      photo: previewData?.curriculumPhoto ? safeUrl(previewData.curriculumPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070"
    },
    activities: (previewData?.activities && previewData.activities.length > 0) ? previewData.activities : [
      { id: "1", title: "Robotics Club", description: "Design and program modern robots with Python.", photo: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop" },
      { id: "2", title: "Drama Society", description: "Annual theatrical productions and national competitions.", photo: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?q=80&w=400&auto=format&fit=crop" },
      { id: "3", title: "Basketball Team", description: "Our varsity team 'The Eagles' are the regional champions.", photo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=400&auto=format&fit=crop" }
    ],
    facilities: (previewData?.facilities && previewData.facilities.length > 0) ? previewData.facilities : [
      { id: "1", categoryId: "Technology", title: "Modern Science Lab", description: "Fully equipped biology and chemistry laboratories.", photoFile: "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=400&auto=format&fit=crop" },
      { id: "2", categoryId: "Academic", title: "Digital Library", description: "Access to over 10,000 e-books and research journals.", photoFile: "https://images.unsplash.com/photo-1521587760476-6c12a7b041da?q=80&w=400&auto=format&fit=crop" },
      { id: "3", categoryId: "Sports", title: "Indoor Sports Arena", description: "Olympic-sized pool and multi-purpose gymnasium.", photoFile: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=400&auto=format&fit=crop" },
      { id: "4", categoryId: "Arts", title: "Creative Arts Center", description: "Soundproof music studios and fine arts workshops.", photoFile: "https://images.unsplash.com/photo-1460518451285-8f16c4f0340b?q=80&w=400&auto=format&fit=crop" },
      { id: "5", categoryId: "Medical", title: "Student Health Clinic", description: "24/7 medical services with trained resident nurses.", photoFile: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=400&auto=format&fit=crop" },
      { id: "6", categoryId: "Residential", title: "Premium Boarding Houses", description: "Comfortable, modern living spaces with high-speed internet.", photoFile: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=400&auto=format&fit=crop" }
    ],
    staff: (previewData?.staffMembers && previewData.staffMembers.length > 0) ? previewData.staffMembers : [
      { id: "1", title: "Mr.", name: "Peter Otieno", bio: "HOD Sciences", photoFile: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Ms.", name: "Sarah Wanjiku", bio: "HOD Humanities", photoFile: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Dr.", name: "David Kimani", bio: "HOD Languages", photoFile: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Mrs.", name: "Mary Atieno", bio: "HOD Mathematics", photoFile: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Mr.", name: "John Kamau", bio: "Sports Director", photoFile: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Ms.", name: "Grace Njeri", bio: "Counselor", photoFile: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=256&auto=format&fit=crop" }
    ],
    board: (previewData?.boardMembers && previewData.boardMembers.length > 0) ? previewData.boardMembers : [
      { id: "1", title: "Rev.", name: "John Mark", role: "Chairman BOM", photoFile: "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Dr.", name: "Alice Mwangi", role: "Treasurer", photoFile: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Hon.", name: "Samuel Korir", role: "Community Rep", photoFile: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Eng.", name: "Francis Njuguna", role: "Infrastructure", photoFile: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Prof.", name: "Jane Kemuntu", role: "Advisor", photoFile: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Lady Justice", name: "Ruth Kerubo", role: "Legal", photoFile: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop" }
    ],
    gallery: (previewData?.galleryItems && previewData.galleryItems.length > 0) ? previewData.galleryItems : [
      { id: "1", categoryId: "Campus", name: "Main Wing", description: "Our historic main administration building.", photoFile: "https://images.unsplash.com/photo-1525921429624-479b6a29d840?q=80&w=600&auto=format&fit=crop" },
      { id: "2", categoryId: "Events", name: "Sports Day", description: "Annual regional championships.", photoFile: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=600&auto=format&fit=crop" },
      { id: "3", categoryId: "Infrastructure", name: "Library", description: "State-of-the-art research center.", photoFile: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=600&auto=format&fit=crop" },
      { id: "4", categoryId: "Students", name: "Tech Club", description: "Students building a robotics prototype.", photoFile: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=600&auto=format&fit=crop" },
      { id: "5", categoryId: "Trips", name: "Mountain Hike", description: "Biology class exploring Mount Kenya flora.", photoFile: "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=600&auto=format&fit=crop" }
    ],
    contact: {
      phones: previewData?.phones || ["+254 712 345 678", "+254 789 012 345"],
      email: previewData?.email || "info@staugustines.ac.ke",
      whatsapp: previewData?.whatsapp || "254712345678",
      facebook: previewData?.fbLink || "",
      twitter: previewData?.twitterLink || "",
      instagram: previewData?.instaLink || "",
      address: previewData?.address || "Main Campus, Kisumu-Kakamega Road, Kisumu",
      mapsLink: previewData?.mapsLink || "https://www.google.com/maps/embed?pb=..."
    },
    admissions: {
      process: previewData?.admissionProcess || "Our intake happens annually in January. Prospective students must submit their applications through our online portal or in-person at the Registrar's Office.",
      requirements: previewData?.admissionRequirements || ["Certified Birth Certificate", "KCPE Result Slip", "Leaving Certificate", "Medical Examination Report"],
      formFile: previewData?.admissionFormFile || null
    },
    payments: (previewData?.paymentMethods && previewData.paymentMethods.length > 0) ? previewData.paymentMethods : [
      { id: "1", method: "M-Pesa Paybill", details: "Business No: 123456 | Account: Student Name" },
      { id: "2", method: "KCB Bank", details: "Acc Name: St. Augustine's | Acc No: 0987654321" }
    ],
    feePolicy: previewData?.feeBreakdown || "School fees are due on or before the first day of each term. Payment plans are available upon request through the Finance Office."
  };

  // ── BACKBONE EFFECTS CONTINUED ────────────────────────────────────────────
  useEffect(() => {
    if (data.headerPhotos.length <= 1) return;
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % data.headerPhotos.length), 5000);
    return () => clearInterval(interval);
  }, [data.headerPhotos.length]);

  const filteredGallery = data.gallery.filter((item: any) => activeTab === 'All' || (item.categoryId || item.category) === activeTab);

  // ── VIBRANT KIDS ORIGINAL THEME EFFECT ─────────────────────────────────────
  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
      const colors = previewData.theme.colors;
      const fonts = previewData.theme.fonts;
      root.style.setProperty('--color-primary', colors.primary || '#4ECDC4');
      root.style.setProperty('--color-secondary', colors.secondary || '#FF6B6B');
      root.style.setProperty('--color-accent', colors.accent || '#FFD700');
      root.style.setProperty('--color-background', colors.background || '#FFFBEB');
      root.style.setProperty('--color-text', colors.text || '#2D3436');
      root.style.setProperty('--font-heading', `'${fonts.heading || 'Fredoka One'}', sans-serif`);
      root.style.setProperty('--font-body', `'${fonts.body || 'Quicksand'}', sans-serif`);
      const radiusMap: any = { none: '0px', small: '8px', medium: '16px', large: '32px', full: '9999px' };
      root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'large']);
    }
    const existingLink = doc.querySelector('link[data-design2-fonts]');
    if (existingLink) existingLink.remove();
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Fredoka+One&family=Quicksand:wght@400;500;600;700&display=swap";
    fontLink.rel = 'stylesheet';
    fontLink.setAttribute('data-design2-fonts', 'true');
    doc.head.appendChild(fontLink);
    const styleTag = doc.createElement('style');
    styleTag.textContent = `
      .vibrant-kids h1, .vibrant-kids h2, .vibrant-kids h3, .vibrant-kids h4 { font-family: var(--font-heading) !important; }
      .vibrant-kids { font-family: var(--font-body) !important; }
      .vibrant-kids .rounded-theme { border-radius: var(--radius) !important; }
    `;
    doc.head.appendChild(styleTag);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);

  const nav = [
    { label: "Home", href: "#" },
    { label: "About", href: "#about" },
    { label: "Academics", href: "#academics" },
    { label: "Gallery", href: "#gallery" },
    { label: "Contact", href: "#contact" }
  ];

  return (
    <div ref={containerRef} className="vibrant-kids text-slate-800 bg-[var(--color-background)] min-h-screen selection:bg-[var(--color-secondary)] selection:text-white">

      {/* ── PLAYFUL HEADER (VibrantKids design) ── */}
      <header className="bg-white py-4 px-6 sticky top-0 z-[60] shadow-sm border-b-4 border-[var(--color-accent)]">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--color-secondary)] rounded-full flex items-center justify-center text-white transform rotate-3 overflow-hidden">
              {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="w-full h-full object-contain p-1" /> : <Sun size={28} />}
            </div>
            <h1 className="text-2xl font-black text-[var(--color-primary)] tracking-tight">{data.name}</h1>
          </div>

          <nav className="hidden lg:flex gap-8 font-bold">
            {nav.map((item, i) => (
              <a key={i} href={item.href} className="text-slate-600 hover:text-[var(--color-secondary)] transition-colors relative group">
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-1 bg-[var(--color-accent)] transition-all group-hover:w-full rounded-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Button
                onClick={() => setShowLoginDropdown(!showLoginDropdown)}
                className="hidden md:flex bg-[var(--color-secondary)] hover:brightness-110 text-white rounded-full px-6 font-bold shadow-[0_4px_0_rgb(0,0,0,0.1)] active:translate-y-1 active:shadow-none transition-all"
              >
                Login <ChevronDown size={16} className={`ml-2 transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`} />
              </Button>
              {showLoginDropdown && (
                <div className="absolute top-full right-0 mt-4 w-64 bg-white rounded-3xl shadow-2xl border-4 border-[var(--color-accent)] p-2 z-[70] overflow-hidden">
                  {[
                    { label: "Parent Portal", href: "#", icon: Heart, color: "text-red-500", bg: "bg-red-50" },
                    { label: "Student Portal", href: "#", icon: Star, color: "text-yellow-500", bg: "bg-yellow-50" }
                  ].map((item, i) => (
                    <a key={i} href={item.href} className="flex items-center gap-3 p-4 hover:bg-slate-50 rounded-2xl transition-all group">
                      <div className={`w-10 h-10 ${item.bg} rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`}>
                        <item.icon size={20} />
                      </div>
                      <span className="font-bold text-slate-700">{item.label}</span>
                      <ArrowRight size={16} className="ml-auto opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-slate-300" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            <button className="lg:hidden text-[var(--color-primary)]" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-[var(--color-primary)] p-8 flex flex-col items-center justify-center text-white lg:hidden">
          <button className="absolute top-6 right-6" onClick={() => setMenuOpen(false)} aria-label="Close menu" title="Close menu"><X size={40} /></button>
          <nav className="flex flex-col gap-6 text-2xl font-black text-center">
            {nav.map((item, i) => (
              <a key={i} href={item.href} onClick={() => setMenuOpen(false)}>{item.label}</a>
            ))}
          </nav>
        </div>
      )}

      {/* ── HERO (VibrantKids design, backbone slideshow) ── */}
      <section className="py-20 px-4 container mx-auto text-center relative overflow-hidden">
        <div className="absolute top-10 left-10 w-32 h-32 bg-[var(--color-accent)] rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-[var(--color-primary)] rounded-full opacity-20 blur-3xl" />

        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="inline-block bg-[var(--color-primary)]/10 text-[var(--color-primary)] px-6 py-2 rounded-full text-sm font-black mb-8 rotate-[-2deg] border-2 border-[var(--color-primary)]/20">
            🎒 Excellence in Education
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 leading-[1.1] tracking-tight">
            Welcome to <br />
            <span className="text-[var(--color-secondary)] underline decoration-wavy decoration-[var(--color-accent)] underline-offset-8">
              {data.name}
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 font-medium max-w-2xl mx-auto leading-relaxed italic">
            "{data.motto}"
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="#admissions" className="bg-[var(--color-primary)] hover:brightness-110 text-white text-sm font-black px-8 py-3 rounded-2xl border-b-4 border-[var(--color-primary)]/30 active:border-b-0 active:translate-y-1 transition-all">
              Admissions
            </a>
            <a href="#contact" className="bg-white hover:bg-slate-50 text-[var(--color-secondary)] text-sm font-black px-8 py-3 rounded-2xl border-2 border-[var(--color-secondary)] transition-all">
              Contact Us
            </a>
          </div>
        </div>

        {/* Backbone slideshow in VibrantKids collage layout */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
          {data.headerPhotos.slice(0, 3).map((img: any, i: number) => (
            <div key={i} className={`group rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white transition-all duration-500 hover:rotate-0 ${i === 1 ? 'scale-110 z-20 rotate-1' : i === 0 ? '-rotate-3 translate-y-8' : 'rotate-3 translate-y-8'}`}>
              <img src={safeUrl(img)} alt={`School ${i}`} className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS (VibrantKids design, backbone data) ── */}
      <section className="py-16 bg-[var(--color-accent)]/10">
        <div className="container mx-auto px-4 flex flex-wrap justify-center gap-12 md:gap-24">
          {[
            { value: data.stats.students, label: "Students" },
            { value: data.stats.staff, label: "Staff Members" },
            { value: data.stats.founded, label: "Est. Year" },
            { value: data.stats.alumni, label: "Alumni" }
          ].map((stat, i) => (
            <div key={i} className="text-center group">
              <div className="text-4xl md:text-5xl font-black text-[var(--color-primary)] mb-2 group-hover:scale-110 transition-transform">
                {stat.value}
              </div>
              <div className="text-sm font-bold text-slate-500 uppercase tracking-widest">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ABOUT / HISTORY (VibrantKids design, backbone data) ── */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-[var(--color-accent)] rounded-[3rem] rotate-3 -z-10" />
            <img
              src={data.aboutPhoto}
              className="rounded-[3rem] shadow-2xl w-full aspect-square md:aspect-video lg:aspect-square object-cover border-8 border-white"
              alt="About"
            />
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-[var(--color-secondary)] rounded-full flex items-center justify-center text-white shadow-xl animate-bounce">
              <Heart size={48} fill="currentColor" />
            </div>
          </div>
          <div>
            <h3 className="text-[var(--color-secondary)] text-2xl font-black mb-4 flex items-center gap-2">
              <Sun fill="currentColor" /> OUR STORY
            </h3>
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-8 leading-tight">
              School History
            </h2>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-medium">{data.history}</p>

            {/* Vision & Mission in VibrantKids style */}
            <div className="grid grid-cols-1 gap-6 mb-10">
              <div className="p-6 bg-[var(--color-primary)]/10 rounded-3xl border-2 border-[var(--color-primary)]/20">
                <div className="w-12 h-12 bg-[var(--color-primary)] text-white rounded-2xl flex items-center justify-center mb-4"><Award size={24} /></div>
                <h4 className="font-black text-xl text-[var(--color-primary)] mb-2">Our Vision</h4>
                <p className="text-slate-600 font-medium leading-relaxed">{data.vision}</p>
              </div>
              <div className="p-6 bg-[var(--color-secondary)]/10 rounded-3xl border-2 border-[var(--color-secondary)]/20">
                <div className="w-12 h-12 bg-[var(--color-secondary)] text-white rounded-2xl flex items-center justify-center mb-4"><Target size={24} /></div>
                <h4 className="font-black text-xl text-[var(--color-secondary)] mb-2">Our Mission</h4>
                <p className="text-slate-600 font-medium leading-relaxed">{data.mission}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[{ title: "Safe Haven", icon: Heart }, { title: "Active Learning", icon: Puzzle }, { title: "Certified Staff", icon: Star }, { title: "Fun Zone", icon: Sun }].map((item, i) => (
                <div key={i} className="flex items-center gap-4 group">
                  <div className="w-12 h-12 bg-[var(--color-accent)]/30 rounded-2xl flex items-center justify-center text-[var(--color-primary)] group-hover:bg-[var(--color-primary)] group-hover:text-white transition-all">
                    <item.icon size={24} />
                  </div>
                  <span className="font-black text-slate-700">{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US & ANTHEMS (VibrantKids design, backbone data) ── */}
      <section className="py-24 bg-[var(--color-background)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">Why Choose Us? 🌟</h2>
            <p className="text-xl text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">{data.whyUs}</p>
          </div>
          {/* Anthems panel in VibrantKids style */}
          <div className="bg-slate-900 rounded-[3rem] p-10 md:p-14 relative overflow-hidden shadow-2xl max-w-4xl mx-auto">
            <div className="absolute top-0 right-0 p-10 opacity-10 rotate-12"><Music size={200} /></div>
            <h4 className="font-black uppercase text-sm tracking-widest text-[var(--color-accent)] mb-8 relative z-10">🎵 Institutional Anthems</h4>
            <div className="grid sm:grid-cols-2 gap-6 relative z-10">
              <div
                onClick={() => setExpandedView('anthems')}
                className="p-8 bg-white/5 rounded-[2rem] border border-white/10 cursor-pointer hover:bg-white/10 transition-all hover:scale-[1.02]"
              >
                <div className="flex justify-between items-center mb-4">
                  <p className="text-lg font-black uppercase tracking-widest text-slate-200">National Anthem</p>
                  <ArrowUpRight size={20} className="text-[var(--color-accent)]" />
                </div>
                <p className="text-sm italic leading-relaxed text-slate-400 line-clamp-2">{data.nationalAnthem}</p>
              </div>
              <div
                onClick={() => setExpandedView('anthems')}
                className="p-8 bg-white/5 rounded-[2rem] border border-white/10 cursor-pointer hover:bg-white/10 transition-all hover:scale-[1.02]"
              >
                <div className="flex justify-between items-center mb-4">
                  <p className="text-lg font-black uppercase tracking-widest text-slate-200">School Anthem</p>
                  <ArrowUpRight size={20} className="text-[var(--color-accent)]" />
                </div>
                <p className="text-sm italic leading-relaxed text-slate-400 line-clamp-2">{data.schoolAnthem}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ACADEMICS / CURRICULUM (VibrantKids design, backbone data) ── */}
      <section id="academics" className="py-24 bg-white border-t-8 border-[var(--color-primary)] border-dashed">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">📚 Our Curriculum</h2>
            <p className="text-xl text-slate-600 font-medium">{data.curriculum.description}</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white aspect-[4/3]">
              <img src={data.curriculum.photo} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Academics" />
            </div>
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border-4 border-transparent hover:border-[var(--color-accent)] transition-all">
                <h4 className="font-black text-[var(--color-primary)] text-xl border-b-4 border-[var(--color-accent)] pb-4 mb-6 flex items-center gap-3"><BookOpen size={24} /> Core Subjects</h4>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.compulsory.map((s: string, i: number) => (
                    <span key={i} className="px-4 py-2 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-full font-black text-sm border-2 border-[var(--color-primary)]/20">{s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-[var(--color-primary)] p-8 rounded-[2.5rem] shadow-xl">
                <h4 className="font-black text-white text-xl border-b-4 border-white/20 pb-4 mb-6 flex items-center gap-3"><ArrowRight size={24} className="text-[var(--color-accent)]" /> Electives & Extras</h4>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.optional.map((s: string, i: number) => (
                    <span key={i} className="px-4 py-2 bg-white/10 text-white rounded-full font-black text-sm border-2 border-white/20">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLUBS & ACTIVITIES (VibrantKids design, backbone data) ── */}
      <section className="py-24 bg-[var(--color-background)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">🏆 Clubs & Activities</h2>
            <p className="text-xl text-slate-600 font-medium">Tailored experiences designed to ignite passion beyond the classroom.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {data.activities.length > 0 ? data.activities.map((a: any, i: number) => (
              <div key={i} className="bg-white rounded-[3rem] overflow-hidden shadow-xl border-4 border-transparent hover:border-[var(--color-accent)] hover:-translate-y-4 transition-all duration-500 group">
                <div className="h-56 relative overflow-hidden bg-slate-100">
                  {a.photo ? <img src={safeUrl(a.photo)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="Activity" /> : <div className="w-full h-full flex items-center justify-center"><Trophy size={40} className="text-slate-300" /></div>}
                </div>
                <div className="p-10">
                  <h3 className="text-2xl font-black text-[var(--color-primary)] mb-4">{a.title}</h3>
                  <p className="text-slate-600 font-medium mb-8 leading-relaxed">{a.description}</p>
                </div>
              </div>
            )) : (
              <div className="md:col-span-3 text-center py-20 bg-white rounded-3xl border-4 border-dashed border-slate-200">
                <Trophy size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-black uppercase tracking-widest text-xs">No Activities Added Yet</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── FACILITIES (VibrantKids design, backbone data + View More) ── */}
      <section className="py-24 bg-white border-t-8 border-[var(--color-accent)] border-dashed">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-20">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">✨ Our Magic Campus</h2>
              <p className="text-xl text-slate-600 font-medium">Safe, colorful spaces designed for adventures and discovery.</p>
            </div>
            <Button className="bg-[var(--color-primary)] text-white px-10 h-14 rounded-2xl font-black shrink-0">Explore More</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {data.facilities.slice(0, 4).map((fac: any, i: number) => (
              <div key={i} className="flex flex-col md:flex-row gap-8 items-center group">
                <div className="w-full md:w-1/2 aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl relative">
                  {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /> : <div className="w-full h-full bg-slate-100 flex items-center justify-center"><Building2 size={48} className="text-slate-300" /></div>}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-4 py-1.5 rounded-full text-xs font-black text-[var(--color-secondary)] shadow-lg">{fac.categoryId}</div>
                </div>
                <div className="w-full md:w-1/2">
                  <h3 className="text-2xl font-black text-[var(--color-primary)] mb-4">{fac.title}</h3>
                  <p className="text-xl text-slate-600 font-medium leading-relaxed">{fac.description}</p>
                </div>
              </div>
            ))}
          </div>
          {data.facilities.length > 4 && (
            <div className="mt-16 text-center">
              <button
                onClick={() => setExpandedView('facilities')}
                className="bg-[var(--color-primary)] text-white text-lg font-black px-12 py-5 rounded-full border-b-[6px] border-[var(--color-primary)]/30 hover:brightness-110 transition-all flex items-center gap-3 mx-auto"
              >
                View More <ArrowUpRight size={20} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── PRINCIPAL (VibrantKids design, backbone data) ── */}
      <section className="py-24 bg-[var(--color-secondary)] text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 p-20 opacity-10 rotate-12"><Quote size={240} /></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2 flex justify-center">
              <div className="w-64 h-64 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white/20">
                {data.principal.photo ? <img src={safeUrl(data.principal.photo)} className="w-full h-full object-cover" alt="Principal" /> : <div className="w-full h-full bg-white/10 flex items-center justify-center"><Users size={60} className="text-white/30" /></div>}
              </div>
            </div>
            <div className="lg:col-span-3 space-y-8 text-center lg:text-left">
              <Quote size={80} className="mx-auto lg:mx-0 opacity-30 fill-current" />
              <h2 className="text-4xl md:text-6xl font-black mb-6">What Our Principal Says</h2>
              <p className="text-2xl md:text-4xl font-bold italic mb-8 leading-snug">"{data.principal.message}"</p>
              <div className="flex items-center justify-center lg:justify-start gap-6">
                <div>
                  <h4 className="text-2xl font-black">{data.principal.name}</h4>
                  <p className="text-[var(--color-accent)] font-bold text-lg uppercase tracking-widest">Chief Principal</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAFF & BOARD (VibrantKids design, backbone data + View More) ── */}
      <section className="py-24 bg-[var(--color-accent)]/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">👩‍🏫 Our Team</h2>
            <p className="text-xl text-slate-600 font-medium">Meet the dedicated people driving our institution forward.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-20">
            {/* Faculty */}
            <div>
              <h4 className="text-sm font-black uppercase tracking-widest text-[var(--color-secondary)] border-l-4 border-[var(--color-secondary)] pl-4 mb-10">Academic Faculty</h4>
              <div className="grid grid-cols-2 gap-8">
                {data.staff.slice(0, 4).map((s: any, i: number) => (
                  <div key={i} className="text-center group">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-[var(--color-secondary)]/10 rounded-full scale-110 group-hover:scale-125 transition-transform duration-500" />
                      <div className="w-40 h-40 mx-auto rounded-full border-8 border-white shadow-xl relative z-10 overflow-hidden">
                        {s.photoFile ? <img src={safeUrl(s.photoFile)} alt={s.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <Users size={40} className="text-slate-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-[var(--color-primary)] mb-1">{s.title} {s.name}</h3>
                    <p className="text-[var(--color-secondary)] font-black uppercase text-xs tracking-widest">{s.bio || "Staff"}</p>
                  </div>
                ))}
              </div>
              {data.staff.length > 4 && (
                <div className="mt-12 text-center">
                  <button
                    onClick={() => setExpandedView('staff')}
                    className="border-4 border-dashed border-slate-300 rounded-full px-10 py-4 font-black text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center gap-2 mx-auto"
                  >
                    View More <ArrowUpRight size={16} />
                  </button>
                </div>
              )}
            </div>
            {/* Board */}
            <div>
              <h4 className="text-sm font-black uppercase tracking-widest text-[var(--color-secondary)] border-l-4 border-[var(--color-secondary)] pl-4 mb-10">Board of Management</h4>
              <div className="grid grid-cols-2 gap-8">
                {data.board.slice(0, 4).map((b: any, i: number) => (
                  <div key={i} className="text-center group">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-[var(--color-primary)]/10 rounded-full scale-110 group-hover:scale-125 transition-transform duration-500" />
                      <div className="w-40 h-40 mx-auto rounded-full border-8 border-white shadow-xl relative z-10 overflow-hidden">
                        {b.photoFile ? <img src={safeUrl(b.photoFile)} alt={b.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <School size={40} className="text-slate-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-[var(--color-primary)] mb-1">{b.title} {b.name}</h3>
                    <p className="text-[var(--color-secondary)] font-black uppercase text-xs tracking-widest">{b.role}</p>
                  </div>
                ))}
              </div>
              {data.board.length > 4 && (
                <div className="mt-12 text-center">
                  <button
                    onClick={() => setExpandedView('board')}
                    className="border-4 border-dashed border-slate-300 rounded-full px-10 py-4 font-black text-slate-500 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all flex items-center gap-2 mx-auto"
                  >
                    View More <ArrowUpRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY (VibrantKids design, backbone data + View More) ── */}
      <section id="gallery" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6">📸 Glimpse of Life</h2>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`px-8 py-3 rounded-full font-black text-lg transition-all border-4 ${activeTab === cat ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)] rotate-[-3deg]' : 'bg-white border-slate-100 text-slate-400 hover:border-[var(--color-accent)]/50'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredGallery.slice(0, 4).map((item: any, i: number) => (
              <div
                key={i}
                onClick={() => setLightboxIndex(i)}
                className="group relative rounded-[2rem] overflow-hidden shadow-lg aspect-square border-4 border-white hover:rotate-2 transition-transform duration-500 cursor-pointer"
              >
                {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white font-black text-xl">{item.name}</p>
                  <p className="text-[var(--color-accent)] font-bold">{item.categoryId || item.category}</p>
                </div>
              </div>
            ))}
          </div>
          {filteredGallery.length > 4 && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setExpandedView('gallery')}
                className="border-4 border-dashed border-slate-300 rounded-full px-12 py-5 font-black text-xl text-slate-500 hover:border-[var(--color-secondary)] hover:text-[var(--color-secondary)] transition-all flex items-center gap-3 mx-auto"
              >
                View More <ArrowUpRight size={20} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── ADMISSIONS & FEES (VibrantKids design, backbone data) ── */}
      <section id="admissions" className="py-24 bg-[var(--color-primary)] text-white relative overflow-hidden">
        <div className="absolute bottom-0 left-0 p-20 opacity-10"><GraduationCap size={300} /></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black mb-6">🎒 Admissions & Fees</h2>
            <p className="text-xl font-bold opacity-80">Everything you need to join our family.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="bg-white/10 rounded-[3rem] p-10 border-4 border-white/20">
              <h3 className="text-2xl font-black mb-6 text-[var(--color-accent)]">Enrollment Process</h3>
              <p className="text-lg font-medium italic leading-relaxed mb-8 border-l-4 border-[var(--color-accent)] pl-6 opacity-90">{data.admissions.process}</p>
              <h4 className="font-black text-sm uppercase tracking-widest mb-5 opacity-70">Requirements</h4>
              <ul className="space-y-4">
                {data.admissions.requirements.map((req: string, i: number) => (
                  <li key={i} className="flex items-center gap-4 font-bold text-lg">
                    <CheckCircle2 size={22} className="text-[var(--color-accent)] shrink-0" /> {req}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-black mb-6 text-[var(--color-accent)]">Fees & Finance</h3>
              {data.payments.map((p: any, i: number) => (
                <div key={i} className="bg-white/10 rounded-3xl p-8 border-4 border-white/20 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-black text-[var(--color-accent)] uppercase tracking-widest mb-1">{p.method}</p>
                    <p className="text-xl font-bold">{p.details}</p>
                  </div>
                  <ArrowUpRight size={24} className="text-white/50 shrink-0" />
                </div>
              ))}
              <div className="bg-white/10 rounded-3xl p-8 border-l-8 border-[var(--color-accent)]">
                <p className="text-xs font-black text-[var(--color-accent)] uppercase tracking-widest mb-3">Fee Policy</p>
                <p className="font-bold opacity-80 leading-relaxed">"{data.feePolicy}"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUOTE & CORE VALUES (VibrantKids design, backbone data) ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="bg-[var(--color-accent)]/10 rounded-[3rem] p-12 border-4 border-[var(--color-accent)]/30 text-center">
              <Quote size={80} className="mx-auto mb-8 text-[var(--color-primary)] opacity-20 fill-current" />
              <p className="text-2xl font-bold italic leading-relaxed text-slate-700 mb-8">"{data.quote.text}"</p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-1 bg-[var(--color-secondary)] rounded-full" />
                <p className="font-black uppercase tracking-widest text-slate-900">{data.quote.author}</p>
              </div>
            </div>
            <div>
              <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-12">💡 Core Values</h2>
              <div className="grid grid-cols-2 gap-4">
                {data.coreValues.map((v: string, i: number) => (
                  <div key={i} className="p-6 rounded-[2rem] bg-[var(--color-background)] border-4 border-transparent hover:border-[var(--color-accent)] hover:rotate-1 transition-all flex items-center gap-4">
                    <div className="w-8 h-8 bg-[var(--color-primary)] rounded-full flex items-center justify-center text-white shrink-0"><Star size={16} fill="currentColor" /></div>
                    <span className="font-black text-slate-700 uppercase tracking-wide text-sm">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER (VibrantKids design, backbone data) ── */}
      <footer id="contact" className="bg-slate-900 pt-24 pb-12 text-white/80 selection:bg-white selection:text-slate-900">
        <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-[var(--color-secondary)] rounded-2xl flex items-center justify-center text-white rotate-3 shadow-lg overflow-hidden">
                {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="w-full h-full object-contain p-1" /> : <Sun size={28} />}
              </div>
              <span className="text-2xl font-black text-white leading-tight">{data.name}</span>
            </div>
            <p className="text-base leading-relaxed mb-8 opacity-70 italic font-medium">"{data.motto}"</p>

            <div className="space-y-4">
              {data.contact.whatsapp && (
                <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" className="bg-green-600/20 border border-green-500/30 text-green-400 px-5 py-3 rounded-2xl font-bold text-sm hover:bg-green-600/30 transition-all flex items-center gap-2 w-fit">
                  <Phone size={16} /> Chat on WhatsApp
                </a>
              )}
              <div className="flex gap-3">
                {data.contact.facebook && <a href={data.contact.facebook} aria-label="Facebook" className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-[var(--color-secondary)] hover:text-white transition-all shadow-inner border border-white/10"><Facebook size={22} /></a>}
                {data.contact.instagram && <a href={data.contact.instagram} aria-label="Instagram" className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-[var(--color-secondary)] hover:text-white transition-all shadow-inner border border-white/10"><Instagram size={22} /></a>}
                {data.contact.twitter && <a href={data.contact.twitter} aria-label="Twitter" className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-[var(--color-secondary)] hover:text-white transition-all shadow-inner border border-white/10"><Globe size={22} /></a>}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-xl font-black text-white mb-8 border-b-4 border-[var(--color-accent)] inline-block">Quick Paths</h4>
            <ul className="space-y-4 font-bold">
              {nav.map((item, i) => (
                <li key={i}><a href={item.href} className="hover:text-[var(--color-accent)] transition-all flex items-center gap-2 group"><ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" /> {item.label}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-black text-white mb-8 border-b-4 border-[var(--color-secondary)] inline-block">Contact Info</h4>
            <ul className="space-y-6">
              <li className="flex gap-4 group">
                <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-[var(--color-accent)] group-hover:scale-110 transition-transform shrink-0"><MapPin size={22} /></div>
                <div>
                  <p className="font-bold text-white uppercase text-xs tracking-widest mb-1">Address</p>
                  <p className="font-medium opacity-80 text-sm">{data.contact.address}</p>
                </div>
              </li>
              {data.contact.phones.map((p: string, i: number) => (
                <li key={i} className="flex gap-4 group">
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-[var(--color-secondary)] group-hover:scale-110 transition-transform shrink-0"><Phone size={22} /></div>
                  <div>
                    <p className="font-bold text-white uppercase text-xs tracking-widest mb-1">Phone {i + 1}</p>
                    <p className="font-medium opacity-80 text-sm">{p}</p>
                  </div>
                </li>
              ))}
              <li className="flex gap-4 group">
                <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-[var(--color-accent)] group-hover:scale-110 transition-transform shrink-0"><Mail size={22} /></div>
                <div>
                  <p className="font-bold text-white uppercase text-xs tracking-widest mb-1">Email</p>
                  <p className="font-medium opacity-80 text-sm">{data.contact.email}</p>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xl font-black text-white mb-8 border-b-4 border-white inline-block">📍 Location</h4>
            <div className="h-44 bg-white/5 rounded-2xl border border-white/10 overflow-hidden relative">
              {data.contact.mapsLink ? (
                <iframe title="School map" src={data.contact.mapsLink} className="w-full h-full border-none grayscale pointer-events-none" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-xs italic">Map not configured</div>
              )}
              <a href={data.contact.mapsLink} aria-label="Open map" className="absolute bottom-2 right-2 bg-[var(--color-secondary)] text-white p-2 rounded-lg"><ArrowUpRight size={14} /></a>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 opacity-60 font-bold text-sm">
          <p>&copy; {new Date().getFullYear()} {data.name}. All Rights Reserved.</p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-12 h-12 rounded-2xl bg-[var(--color-primary)] flex items-center justify-center hover:brightness-110 transition-all" aria-label="Back to top" title="Back to top">
            <ChevronDown className="text-white rotate-180" size={24} />
          </button>
        </div>
      </footer>

      {/* ── EXPANDED DIRECTORY OVERLAY (backbone logic, VibrantKids style) ── */}
      {expandedView && (
        <div className="fixed inset-0 z-[200] bg-[var(--color-background)] overflow-y-auto pb-24">
          <div className="bg-white border-b-4 border-[var(--color-accent)] px-6 py-6 flex items-center justify-between sticky top-0 z-50 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => setExpandedView(null)} className="w-12 h-12 rounded-full border-4 border-slate-200 flex items-center justify-center hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all" aria-label="Go back" title="Go back">
                <ChevronLeft size={24} />
              </button>
              <div>
                <h2 className="text-2xl font-black text-slate-900">
                  {expandedView === 'staff' ? '👩‍🏫 Faculty Directory' : expandedView === 'gallery' ? '📸 Visual Archive' : expandedView === 'facilities' ? '✨ All Facilities' : expandedView === 'anthems' ? '🎵 Anthems' : '🏛️ Board of Management'}
                </h2>
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mt-0.5">{data.name}</p>
              </div>
            </div>
            <button onClick={() => setExpandedView(null)} className="text-slate-400 hover:text-slate-900 p-2" aria-label="Close overlay" title="Close overlay"><X size={26} /></button>
          </div>

          <div className="max-w-7xl mx-auto px-6 py-16">
            {expandedView === 'gallery' ? (
              <>
                <div className="flex flex-wrap gap-3 justify-center mb-10">
                  {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                    <button
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      className={`px-8 py-3 rounded-full font-black text-lg transition-all border-4 ${activeTab === cat ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]' : 'bg-white border-slate-200 text-slate-400 hover:border-[var(--color-accent)]/50'}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredGallery.map((item: any, i: number) => (
                    <div
                      key={i}
                      onClick={() => setLightboxIndex(i)}
                      className="group relative rounded-[2rem] overflow-hidden shadow-lg aspect-square border-4 border-white hover:rotate-2 transition-transform duration-500 cursor-pointer"
                    >
                      {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
                      <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white font-black text-xl">{item.name}</p>
                        <p className="text-[var(--color-accent)] font-bold">{item.categoryId || item.category}</p>
                        {item.description && <p className="text-white/70 text-sm mt-1 line-clamp-2">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : expandedView === 'facilities' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {data.facilities.map((fac: any, i: number) => (
                  <div key={i} className="flex flex-col md:flex-row gap-8 items-center group bg-white rounded-[2.5rem] p-6 shadow-lg border-4 border-transparent hover:border-[var(--color-accent)] transition-all">
                    <div className="w-full md:w-1/2 aspect-video rounded-[2rem] overflow-hidden relative">
                      {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /> : <div className="w-full h-full bg-slate-100 flex items-center justify-center"><Building2 size={40} className="text-slate-300" /></div>}
                      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-black text-[var(--color-secondary)]">{fac.categoryId}</div>
                    </div>
                    <div className="w-full md:w-1/2">
                      <h3 className="text-2xl font-black text-[var(--color-primary)] mb-3">{fac.title}</h3>
                      <p className="text-slate-600 font-medium leading-relaxed">{fac.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : expandedView === 'anthems' ? (
              <div className="max-w-4xl mx-auto space-y-20">
                {/* National Anthem */}
                <div className="space-y-10">
                  <div className="text-center space-y-4">
                    <span className="text-[var(--color-secondary)] font-black uppercase tracking-widest text-sm">The Republic of Kenya</span>
                    <h3 className="text-4xl md:text-5xl font-black text-slate-900">National Anthem</h3>
                  </div>
                  <div className="aspect-video w-full rounded-[3rem] overflow-hidden shadow-2xl relative">
                    <img src="https://images.unsplash.com/photo-1608925565154-2974eb36e6ba?q=80&w=2070" className="w-full h-full object-cover" alt="National Anthem backdrop" />
                    <div className="absolute inset-0 bg-slate-900/40" />
                  </div>
                  <div className="bg-white p-10 md:p-14 rounded-[3rem] border-4 border-[var(--color-accent)]/30 text-center shadow-lg">
                    <p className="whitespace-pre-line text-xl text-slate-700 leading-loose italic font-medium">{data.nationalAnthem}</p>
                  </div>
                </div>
                {/* School Anthem */}
                <div className="space-y-10 pt-14 border-t-4 border-dashed border-[var(--color-accent)]">
                  <div className="text-center space-y-4">
                    <span className="text-[var(--color-secondary)] font-black uppercase tracking-widest text-sm">{data.name}</span>
                    <h3 className="text-4xl md:text-5xl font-black text-slate-900">School Anthem</h3>
                  </div>
                  <div className="aspect-video w-full rounded-[3rem] overflow-hidden shadow-2xl relative">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070" className="w-full h-full object-cover" alt="School Anthem backdrop" />
                    <div className="absolute inset-0 bg-slate-900/40" />
                  </div>
                  <div className="bg-white p-10 md:p-14 rounded-[3rem] border-4 border-[var(--color-primary)]/20 text-center shadow-lg">
                    <p className="whitespace-pre-line text-xl text-slate-700 leading-loose italic font-medium">{data.schoolAnthem}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                {(expandedView === 'staff' ? data.staff : data.board).map((item: any, i: number) => (
                  <div key={i} className="flex flex-col items-center text-center space-y-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[var(--color-secondary)]/10 rounded-full scale-110" />
                      <div className="w-44 h-44 mx-auto rounded-full border-8 border-white shadow-xl relative z-10 overflow-hidden bg-slate-100">
                        {(item.photoFile || item.photo) ? (
                          <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Profile"} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300">
                            {expandedView === 'staff' ? <Users size={52} /> : <School size={52} />}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-black text-[var(--color-primary)] text-xl mb-1">{item.title} {item.name}</h3>
                      <p className="text-[var(--color-secondary)] font-black uppercase text-xs tracking-widest">{expandedView === 'staff' ? (item.bio || 'Faculty') : item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LIGHTBOX (backbone logic, VibrantKids style) ── */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div className="fixed inset-0 bg-black flex items-center justify-center" style={{ zIndex: 99999 }}>
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-8 left-8 z-50 text-white/70 hover:text-white transition-all flex items-center gap-2 font-black uppercase tracking-widest text-sm bg-black/40 px-6 py-3 rounded-full border-2 border-white/10"
          >
            <ChevronLeft size={16} /> Back to Gallery
          </button>
          <div className="absolute inset-0 w-full h-full">
            {(filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo ? (
              <img src={safeUrl((filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo)} alt={(filteredGallery[lightboxIndex] as any).name || "Gallery"} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full bg-slate-900 flex items-center justify-center text-white/20"><ImageIcon size={80} /></div>
            )}
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 bg-black/70 backdrop-blur-xl border-4 border-[var(--color-accent)]/30 px-10 py-6 rounded-[2rem] max-w-2xl w-[90%] text-center shadow-2xl">
            <p className="text-xs font-black text-[var(--color-accent)] uppercase tracking-widest mb-1">{(filteredGallery[lightboxIndex] as any).categoryId || (filteredGallery[lightboxIndex] as any).category}</p>
            <h3 className="text-white font-black text-2xl tracking-tight leading-tight mb-2">{(filteredGallery[lightboxIndex] as any).name || "Untitled"}</h3>
            <p className="text-white/60 text-sm leading-relaxed hidden md:block">{(filteredGallery[lightboxIndex] as any).description || ""}</p>
          </div>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-between px-6 md:px-12 z-20">
            {lightboxIndex > 0 ? (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 p-6 rounded-full font-black uppercase text-sm border-2 border-white/10"
              >
                Prev
              </button>
            ) : <div />}
            {lightboxIndex < filteredGallery.length - 1 ? (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 p-6 rounded-full font-black uppercase text-sm border-2 border-white/10"
              >
                Next
              </button>
            ) : <div />}
          </div>
        </div>
      )}
    </div>
  );
};

export default VibrantKids;
